import React from 'react';
import { PDFPageProps, FileType } from './types';
import { StyledPageWrapper } from './StyledComponents';

const PDFPage = <T extends FileType>({
  file,
  page,
  width,
  onPageDrawn,
  render,
}: PDFPageProps<T>) => {
  const [canvas, setCanvas] = React.useState<HTMLCanvasElement>(null);
  const drawing = React.useRef(false);
  const drawnWidth = React.useRef<number>(null);

  const drawPage = async () => {
    if (!canvas || drawing.current) return;

    drawing.current = true;

    const context = canvas.getContext('2d');
    const rawViewport = page.getViewport({ scale: 1 });

    const containerWidth = width;
    const scale = containerWidth / rawViewport.width;

    const viewport = page.getViewport({ scale });

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    drawnWidth.current = width;

    await page.render({
      canvasContext: context,
      viewport,
    }).promise;

    drawing.current = false;
    onPageDrawn();
  };

  const onWidthChange = () => {
    // This makes the pdf re-draw in a larger view if the difference is considerable
    if (!drawnWidth.current) return drawPage();
    const difference = (width - drawnWidth.current) / drawnWidth.current;
    const threshold = 0.2;
    if (difference > threshold) drawPage();
  };

  React.useEffect(() => {
    drawPage();
  }, [page, canvas]);

  React.useEffect(() => {
    onWidthChange();
  }, [width]);

  const Canvas = React.useMemo(() => (
      <StyledPageWrapper>
        <canvas ref={setCanvas} />
      </StyledPageWrapper>
    ), []);

  return render ? render(Canvas, file, page) : Canvas;
};
export default PDFPage;
